import React from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Typography, Button } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ErrorOutline } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { PropTypes } from "prop-types";

const useStyles = makeStyles(theme => ({
  textContainer: {
    "& > *": {
      paddingBottom: theme.spacing(2.25)
    }
  },
  title: {
    paddingBottom: theme.spacing(3)
  },
  questionButton: {
    alignItems: "baseline"
  }
}));

const UnansweredQuestionsList = ({ questions, onQuestionClick }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  return (
    <Box
      display="flex"
      flexDirection="column"
      mt={2}
      alignItems="flex-start"
      data-qa="submission-confirmation-list"
    >
      {questions.map(({ questionNumber, mandatory }, index) => (
        <Button
          key={index}
          color="primary"
          onClick={onQuestionClick}
          className={classes.questionButton}
        >
          <Typography variant="h6" color="primary">
            {t("courseQuiz.submissionConfirmation.questionN", {
              questionNumber
            })}
          </Typography>
          {mandatory && (
            <Typography
              variant="subtitle2"
              color="error"
              className="italic"
              sx={{ pl: 1.5 }}
            >
              {t("courseQuiz.submissionConfirmation.mandatory")}
            </Typography>
          )}
        </Button>
      ))}
    </Box>
  );
};

const SubmissionWarning = ({ prevStep, unansweredQuestions }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const numMandatoryQuestions = unansweredQuestions.filter(
    question => question.mandatory
  ).length;
  const hasMandatoryQuestions = numMandatoryQuestions > 0;
  const hasOptionalQuestions =
    numMandatoryQuestions < unansweredQuestions.length;

  const showUnasweredQuestionsWarning =
    hasMandatoryQuestions || hasOptionalQuestions;

  const warningHeader = hasMandatoryQuestions
    ? t("courseQuiz.submissionConfirmation.pleaseCorrect")
    : t("courseQuiz.submissionConfirmation.warnings");

  const warningMessage = `${
    hasMandatoryQuestions && hasOptionalQuestions // show message for both unanswered mandatory and optional questions
      ? t("courseQuiz.submissionConfirmation.unansweredMsg", {
          numQuestions: unansweredQuestions.length,
          numMandatory: numMandatoryQuestions
        })
      : hasMandatoryQuestions // Show message for only unanswered mandatory questions
      ? t("courseQuiz.submissionConfirmation.unansweredMandatoryMsg", {
          numUnansweredQuestions: unansweredQuestions.length
        })
      : // Show message for only unanswered optional questions
        t("courseQuiz.submissionConfirmation.unansweredOptionalMsg", {
          numUnansweredQuestions: unansweredQuestions.length
        })
  }${
    hasMandatoryQuestions // Show submit message if there are mandatory questions
      ? `. ${t("courseQuiz.submissionConfirmation.submitAnswersMsg")}`
      : ""
  }:`;

  return showUnasweredQuestionsWarning > 0 ? (
    <Box
      display="flex"
      flexDirection="row"
      bgcolor={theme.palette.secondary[50]}
      p={{ xs: 2, sm: 3 }}
      mb={4}
      data-qa="submission-confirmation-unanswered-questions"
    >
      <Box fontSize="1.75rem">
        <ErrorOutline color="error" fontSize="inherit" />
      </Box>
      <Box display="flex" flexDirection="column" ml={{ xs: 2, sm: 3 }}>
        <Typography
          variant="h4"
          color="error"
          data-qa="submission-confirmation-header"
        >
          {warningHeader}
        </Typography>
        <Box display="flex" flexDirection="column" pt={2.5}>
          <Typography
            variant="h6"
            color="textSecondary"
            data-qa="submission-confirmation-message"
          >
            {warningMessage}
          </Typography>
          <UnansweredQuestionsList
            questions={unansweredQuestions}
            onQuestionClick={prevStep}
          />
        </Box>
      </Box>
    </Box>
  ) : null;
};

const SubmissionText = ({ quizType }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <Box
      display="flex"
      flexDirection="column"
      className={classes.textContainer}
      data-qa="submission-confirmation-text"
    >
      <Typography variant="h4" color="textSecondary" className={classes.title}>
        {`${quizType} ${t("courseQuiz.submissionConfirmation.title")}`}
      </Typography>
      <Typography variant="body1" color="textSecondary">
        {t("courseQuiz.submissionConfirmation.submissionMsg", {
          quizType: quizType.toString().toLowerCase()
        })}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {t("courseQuiz.submissionConfirmation.noReturnWarning", {
          quizType: quizType.toString().toLowerCase(),
          quizTypeBtn: quizType
        })}
      </Typography>
    </Box>
  );
};

const SubmissionConfirmation = ({
  prevStep,
  unansweredQuestions,
  quizType
}) => {
  const showConfirmationMessage =
    unansweredQuestions.filter(question => question.mandatory).length === 0;
  return (
    <Box display="flex" flexDirection="column">
      <SubmissionWarning
        prevStep={prevStep}
        unansweredQuestions={unansweredQuestions}
      />
      {showConfirmationMessage && <SubmissionText quizType={quizType} />}
    </Box>
  );
};

export default SubmissionConfirmation;

SubmissionConfirmation.propTypes = {
  unansweredQuestions: PropTypes.arrayOf(
    PropTypes.shape({
      questionNumber: PropTypes.number,
      mandatory: PropTypes.bool
    })
  )
};
